import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Alert,
  ActivityIndicator,
} from 'react-native';

import OrnamentBackground from '../../components/OrnamentBackground';
import { API_URL } from '../../config';
import { getAuthToken } from '../../services/auth/tokenStorage';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('EmailVerificationNotice');

interface EmailVerificationNoticeScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<{ params: { email: string } }, 'params'>;
}

const EmailVerificationNoticeScreen: React.FC<EmailVerificationNoticeScreenProps> = ({
  navigation,
  route,
}) => {
  const { email } = route.params;
  const [isResending, setIsResending] = useState(false);

  // Poll for email verification status every 3 seconds
  useEffect(() => {
    if (!email) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/auth/email-verification-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        });

        if (res.ok) {
          const data = await res.json();
          if (data.isEmailVerified) {
            clearInterval(interval);
            // The link may have been opened on another device (a phone tapping
            // the email while this tab waits). Only that device received a
            // session, so here there is no token and the app would render
            // signed out. Send this device to Login instead of into the app.
            if (!(await getAuthToken())) {
              navigation.reset({
                index: 0,
                routes: [{ name: 'Login', params: { email, verifiedElsewhere: true } }],
              });
              return;
            }
            if ((data.onboardingVersion ?? 0) >= 2) {
              // Letter-first signup: verification leads straight to the
              // letter. Security is offered post-upgrade (Phase 8), so the
              // vault Security Chooser (and its skip countdown) never
              // shows here.
              navigation.navigate('LetterFirst', { screen: 'LetterHook' });
            } else {
              // Invited accounts return to their invitation; anyone else
              // lands on the Dashboard. Two-factor is offered after an
              // upgrade, never here.
              const invitationToken = await AsyncStorage.getItem('@invitation_token');
              if (invitationToken) {
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'AcceptInvitation', params: { token: invitationToken } }],
                });
              } else {
                navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });
              }
            }
          }
        }
      } catch (err) {
        logger.error('Error checking email verification status:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [email, navigation]);

  const handleResendEmail = async () => {
    setIsResending(true);

    try {
      const response = await fetch(`${API_URL}/auth/send-email-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (response.ok) {
        Alert.alert(
          'Email Sent',
          'A new verification email has been sent. Please check your inbox.'
        );
      } else {
        Alert.alert(
          'Error',
          result.message || 'Failed to resend verification email. Please try again.'
        );
      }
    } catch (error) {
      logger.error('Failed to resend verification email:', error);
      Alert.alert('Error', 'Network error. Please check your connection and try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleChangeEmail = () => {
    // Go back to SignUp screen to change email
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <Text style={styles.iconText}>📧</Text>
        </View>

        <View style={styles.header}>
          <Text style={styles.title}>Check Your Email</Text>
          <Text style={styles.subtitle}>
            We've sent a verification link to{'\n'}
            <Text style={styles.emailText}>{email}</Text>
          </Text>
        </View>

        <View style={styles.instructionsContainer}>
          <Text style={styles.instructionsTitle}>Next Steps:</Text>
          <View style={styles.instructionItem}>
            <Text style={styles.stepNumber}>1.</Text>
            <Text style={styles.instructionText}>
              Open your email inbox
            </Text>
          </View>
          <View style={styles.instructionItem}>
            <Text style={styles.stepNumber}>2.</Text>
            <Text style={styles.instructionText}>
              Find the email from MyFinalKey
            </Text>
          </View>
          <View style={styles.instructionItem}>
            <Text style={styles.stepNumber}>3.</Text>
            <Text style={styles.instructionText}>
              Click the verification link
            </Text>
          </View>
        </View>

        <View style={styles.infoBox}>
          <Text style={styles.infoText}>
            The verification link will expire in 24 hours.
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.resendButton, isResending && styles.resendButtonDisabled]}
          onPress={handleResendEmail}
          disabled={isResending}
        >
          {isResending ? (
            <ActivityIndicator color="#29BFC0" />
          ) : (
            <Text style={styles.resendButtonText}>Resend Verification Email</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.changeEmailButton}
          onPress={handleChangeEmail}
        >
          <Text style={styles.changeEmailText}>Change Email Address</Text>
        </TouchableOpacity>

        <View style={styles.helpContainer}>
          <Text style={styles.helpText}>
            Didn't receive the email? Check your spam folder or{' '}
            <Text style={styles.helpLink} onPress={handleResendEmail}>
              resend verification email
            </Text>
          </Text>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  content: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    marginTop: ms(60),
    paddingHorizontal: ms(20),
    paddingTop: ms(40),
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: ms(20),
  },
  iconText: {
    fontSize: ms(64, 0.3),
  },
  header: {
    alignItems: 'center',
    marginBottom: ms(30),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(15),
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
  },
  emailText: {
    fontWeight: '600',
    color: '#29BFC0',
  },
  instructionsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
  },
  instructionsTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(15),
  },
  instructionItem: {
    flexDirection: 'row',
    marginBottom: ms(12),
  },
  stepNumber: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
    marginRight: ms(10),
    width: s(20),
  },
  instructionText: {
    fontSize: ms(16, 0.3),
    color: '#666',
    flex: 1,
  },
  infoBox: {
    backgroundColor: '#FFF3CD',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(25),
  },
  infoText: {
    fontSize: ms(14, 0.3),
    color: '#856404',
    textAlign: 'center',
  },
  resendButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#29BFC0',
    paddingVertical: ms(16),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(15),
  },
  resendButtonDisabled: {
    opacity: 0.6,
  },
  resendButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  changeEmailButton: {
    alignItems: 'center',
    paddingVertical: ms(10),
    marginBottom: ms(20),
  },
  changeEmailText: {
    color: '#666',
    fontSize: ms(15, 0.3),
  },
  helpContainer: {
    paddingHorizontal: ms(10),
    paddingTop: ms(10),
  },
  helpText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
  helpLink: {
    color: '#29BFC0',
    fontWeight: '600',
  },
});

export default EmailVerificationNoticeScreen;

